import { useState } from "react";
import { AvatarPersona } from "@/components/ui/avatar-persona";
import { useFotoPerfilVista } from "@/hooks/useFotoPerfilVista";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  FileText,
  Landmark,
  Stamp,
  CalendarDays,
  PackageCheck,
  Menu,
  LucideIcon,
  Receipt,
  ShieldAlert,
  Scale,
  HeartHandshake,
  GitBranch,
  FileSearch,
  ClipboardList,
  CheckSquare,
  Settings,
  FileSignature,
} from "lucide-react";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { useAuth } from "@/contexts/AuthContext";
import { useCanReturnToAdmin } from "@/hooks/useCanReturnToAdmin";
import { useAllowedMenus } from "@/hooks/useAllowedMenus";
import { usePortalNavEstado } from "@/hooks/usePortalNav";
import { puedeAbrirRutaPortal } from "@/lib/routeAccess";
import { PortalTrackingProvider } from "@/contexts/PortalTrackingContext";
import { PortalSidebar, PORTAL_SIDEBAR_ASIDE_CLS, PORTAL_SIDEBAR_SHEET_CLS } from "@/components/admin/PortalSidebar";

const BASE = "/admin/portal-escrituracion";

/**
 * Menú "Portal de escrituración" en BD (`menus.id`, igual en dev y prod). Nombre, orden y
 * alta/baja de cada vista viven en `submenus`: se administran en Administrar menús y se asignan
 * por rol en Roles y permisos.
 */
const ESCRITURACION_MENU_ID = 20;

/** El ícono no vive en BD: se resuelve por ruta. Rutas nuevas sin ícono caen en `FileText`. */
const iconMap: Record<string, LucideIcon> = {
  [`${BASE}/dashboard`]:                  LayoutDashboard,
  [`${BASE}/expedientes`]:                FileText,
  [`${BASE}/relacion-pagos`]:             Receipt,
  [`${BASE}/unidades-listas-escriturar`]: CheckSquare,
  [`${BASE}/notarias`]:                   Stamp,
  [`${BASE}/pld`]:                        ShieldAlert,
  [`${BASE}/credito`]:                    Landmark,
  [`${BASE}/citas`]:                      CalendarDays,
  [`${BASE}/demandas`]:                   Scale,
  [`${BASE}/entregas`]:                   PackageCheck,
  [`${BASE}/checklist-configuracion`]:    ClipboardList,
  [`${BASE}/postventa`]:                  HeartHandshake,
  [`${BASE}/workflow`]:                   GitBranch,
  [`${BASE}/validacion-contratos`]:       FileSearch,
  [`${BASE}/plantillas`]:                 FileSignature,
  [`${BASE}/configuracion`]:              Settings,
};

export const PortalEscrituracionLayout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { profile, signOut } = useAuth();
  const { canReturnToAdmin } = useCanReturnToAdmin();
  const { items: navFromDb, cargando: navCargando } =
    usePortalNavEstado(ESCRITURACION_MENU_ID, iconMap, FileText);
  const { isPathDisabled, allowedPaths, enabledPaths, isSuperAdmin, isLoading: permisosCargando } =
    useAllowedMenus();
  const [mobileOpen, setMobileOpen] = useState(false);

  // El menú sale de BD y se filtra con la misma decisión que PermissionRoute (permiso exacto por
  // vista): el sidebar no ofrece vistas apagadas ni vistas que la ruta va a negar.
  const visibleNavItems = navFromDb.filter(
    (i) =>
      !isPathDisabled(i.path) &&
      puedeAbrirRutaPortal(i.path, {
        rolId: profile?.rol_id,
        rolNombre: profile?.rol_nombre,
        allowedPaths,
        isSuperAdmin,
        hasEmbajadorRole: false,
        hasSocioRole: false,
        rutasRegistradas: enabledPaths,
      }),
  );

  const handleNavigate = (path: string) => {
    navigate(path);
    setMobileOpen(false);
  };

  const isActive = (path: string) =>
    location.pathname === path || location.pathname.startsWith(path + "/");

  const currentSection = visibleNavItems.find((i) => isActive(i.path))?.label || "Escrituración";

  const rawName = profile?.nombre || profile?.email?.split("@")[0] || "Usuario";
  const userName = rawName.trim().split(/\s+/).slice(0, 2).join(" ");
  const userRole = profile?.rol_nombre ?? "Escrituración";
  const initials = userName.split(" ").filter(Boolean).slice(0, 2).map((p: string) => p.charAt(0).toUpperCase()).join("") || "U";
  const fotoVista = useFotoPerfilVista();

  const sidebar = (
    <PortalSidebar
      portal="Portal Escrituración"
      cargando={navCargando || permisosCargando}
      groups={[{
        items: visibleNavItems.map((item) => ({
          key: item.path,
          label: item.label,
          icon: item.icon,
          active: isActive(item.path),
          onSelect: () => handleNavigate(item.path),
        })),
      }]}
      usuario={{ nombre: userName, rol: userRole, iniciales: initials }}
      onRegresar={canReturnToAdmin ? () => navigate("/admin") : undefined}
      onCerrarSesion={() => signOut()}
    />
  );

  return (
    <PortalTrackingProvider portal="escrituracion">
      <div className="min-h-screen flex antialiased">
        {/* Desktop sidebar */}
        <aside className={PORTAL_SIDEBAR_ASIDE_CLS}>
          {sidebar}
        </aside>

        {/* Mobile drawer */}
        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
          <SheetContent side="left" className={PORTAL_SIDEBAR_SHEET_CLS}>
            {sidebar}
          </SheetContent>
        </Sheet>

        <div className="flex-1 lg:pl-64 min-w-0">
          {/* Mobile header */}
          <header className="flex lg:hidden flex-col sticky top-0 z-20 bg-card border-b border-border">
            <div className="flex items-center px-4 pt-3 pb-2 gap-3">
              <button
                onClick={() => setMobileOpen(true)}
                className="p-1.5 -ml-1 rounded-md text-foreground hover:bg-muted transition-colors"
                aria-label="Abrir menú"
              >
                <Menu className="h-5 w-5" />
              </button>
              <div className="min-w-0 flex-1">
                <p className="text-[15px] font-semibold text-foreground tracking-tight truncate">{currentSection}</p>
              </div>
              <AvatarPersona nombre={userName} fotoUrl={fotoVista} size="sm" />
            </div>
          </header>

          <main className="px-8 py-4 bg-background min-h-screen">
            <Outlet />
          </main>
        </div>
      </div>
    </PortalTrackingProvider>
  );
};

export default PortalEscrituracionLayout;
